// components/FacturarGastosCancelacion.js
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Form,
  Input,
  Select,
  InputNumber,
  Space,
  Typography,
  message,
  Card,
  Row,
  Col,
  Divider,
  Tag,
  Descriptions,
  Alert,
  Spin,
  Table,
  Tooltip,
  Badge
} from 'antd';
import {
  DollarOutlined,
  FileTextOutlined,
  UserOutlined,
  BankOutlined,
  PrinterOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  EditOutlined,
  SaveOutlined,
  SyncOutlined,
  TagOutlined,
  ReloadOutlined,
  FilePdfOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { BASE_URL } from '../utils/constants';

import { reportService,generateAndDisplayReport } from '../services/reportService2';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const FacturarGastosCancelacion = ({ 
  visible, 
  reserva, 
  onCancel, 
  onSuccess,
  onImprimir,
  onTipoCancelacionActualizado 
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [importe, setImporte] = useState(0);
  const [aQuienFactura, setAQuienFactura] = useState('Particular');
  const [tiposCancelacion, setTiposCancelacion] = useState([]);
  const [facturaExistente, setFacturaExistente] = useState(null);
  const [paso, setPaso] = useState(1);
  const [resultado, setResultado] = useState(null);
  const [cargandoTipos, setCargandoTipos] = useState(false);
  const [editandoTipo, setEditandoTipo] = useState(false);
  const [tipoSeleccionado, setTipoSeleccionado] = useState(null);
  const [actualizandoTipo, setActualizandoTipo] = useState(false);
  const [tipoOriginal, setTipoOriginal] = useState(null);
  const [cargandoReserva, setCargandoReserva] = useState(false);
  const [reservaActual, setReservaActual] = useState(null);
  const [tipoInfoActual, setTipoInfoActual] = useState(null);
  const [verificandoFactura, setVerificandoFactura] = useState(false);
  const [facturaYaExiste, setFacturaYaExiste] = useState(false);
  const [mostrandoDetalleFactura, setMostrandoDetalleFactura] = useState(false);

  // Cargar tipos de cancelación, datos de la reserva y verificar factura al abrir el modal
  useEffect(() => {
    if (visible && reserva) {
      // Resetear estados
      setPaso(1);
      setResultado(null);
      setEditandoTipo(false);
      setActualizandoTipo(false);
      setFacturaYaExiste(false);
      setFacturaExistente(null);
      setMostrandoDetalleFactura(false);
      
      // Cargar todos los datos
      cargarTiposCancelacion();
      cargarDatosReserva(reserva.NumReserva);
      verificarFacturaExistente(reserva.NumReserva);
      
      // Establecer valores por defecto
      const tipoInicial = reserva.TipoCancelacion ? String(reserva.TipoCancelacion) : null;
      setTipoSeleccionado(tipoInicial);
      setTipoOriginal(tipoInicial);
      
      form.setFieldsValue({
        importe: 0,
        aQuien: 'Particular',
        motivo: `Gastos de cancelación - Reserva #${reserva.NumReserva}`,
        tipoCancelacion: tipoInicial
      });
      setImporte(0);
      setAQuienFactura('Particular');
      
      // Si no tiene tipo, activar edición automáticamente
      if (!reserva.TipoCancelacion) {
        setEditandoTipo(true);
      }
    }
  }, [visible, reserva]);

  // Actualizar la información del tipo seleccionado cuando cambia el tipo o la lista de tipos
  useEffect(() => {
    if (tipoSeleccionado && tiposCancelacion.length > 0) {
      const info = tiposCancelacion.find(t => String(t.Codigo) === String(tipoSeleccionado));
      setTipoInfoActual(info || null);
    } else {
      setTipoInfoActual(null);
    }
  }, [tipoSeleccionado, tiposCancelacion]);

  // Cargar datos actualizados de la reserva
  const cargarDatosReserva = async (numReserva) => {
    setCargandoReserva(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/reservas/${numReserva}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const data = response.data.data;
        setReservaActual(data);
        if (data.TipoCancelacion) {
          const nuevoTipo = String(data.TipoCancelacion);
          setTipoSeleccionado(nuevoTipo);
          setTipoOriginal(nuevoTipo);
          form.setFieldsValue({ tipoCancelacion: nuevoTipo });
          
          const info = tiposCancelacion.find(t => String(t.Codigo) === nuevoTipo);
          setTipoInfoActual(info || null);
        }
      }
    } catch (error) {
      console.error('Error cargando datos de reserva:', error);
    } finally {
      setCargandoReserva(false);
    }
  };

  const cargarTiposCancelacion = async () => {
    setCargandoTipos(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/tipos-cancelacion`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const tipos = response.data.data.map(tipo => ({
          ...tipo,
          Codigo: String(tipo.Codigo)
        }));
        setTiposCancelacion(tipos);
        
        if (tipoSeleccionado) {
          const info = tipos.find(t => t.Codigo === String(tipoSeleccionado));
          setTipoInfoActual(info || null);
        }
      }
    } catch (error) {
      console.error('Error cargando tipos de cancelación:', error);
      message.error('Error al cargar los tipos de cancelación');
    } finally {
      setCargandoTipos(false);
    }
  };

  // Verificar si existe factura de cancelación
  const verificarFacturaExistente = async (numReserva) => {
    setVerificandoFactura(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BASE_URL}/api/reservas/factura-cancelacion/${numReserva}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setFacturaExistente(response.data.data);
        setFacturaYaExiste(true);
        // Si ya existe factura, mostrar el detalle directamente en el modal
        setMostrandoDetalleFactura(true);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setFacturaYaExiste(false);
        setFacturaExistente(null);
        setMostrandoDetalleFactura(false);
      } else {
        console.error('Error verificando factura:', error);
      }
    } finally {
      setVerificandoFactura(false);
    }
  };

const handleImprimir = async (factura) => {
      console.log(" factura => ",factura);
      
      if (!factura || !factura.NFactura) {
        Modal.error({
          title: 'Error',
          content: 'Número de factura no válido'
        });
        return;
      }
      
      const ficheroReport = "Factura Hotel "+factura.CodigoHotel;

      console.log( " ficheroReport => ",ficheroReport);
      
      try {
        await generateAndDisplayReport({
          hotelCode: factura.CodigoHotel,
          reportName: ficheroReport,
          sqlQuery: ` Select  serie + cast(nfactura as varchar) + cast(codigohotel as varchar) + cast(ejercicio as varchar) facturaunico , * from CabeceraFactura 
                      where CodigoHotel = ${factura.CodigoHotel} and nfactura = ${factura.NFactura}
                        and Ejercicio = ${factura.Ejercicio}
                        and Serie = '${factura.Serie}' `,
          // token: localStorage.getItem('token') // Opcional
        });
      } catch (error) {
        // El error ya se maneja en el hook
        console.error('Error:', error);
      }
  };  

  // Actualizar tipo de cancelación
  const handleActualizarTipoCancelacion = async () => {
    try {
      const values = await form.validateFields(['tipoCancelacion']);
      const nuevoTipo = values.tipoCancelacion;
      
      if (!nuevoTipo) {
        message.warning('Por favor seleccione un tipo de cancelación');
        return;
      }
      
      const tipo = tiposCancelacion.find(t => String(t.Codigo) === String(nuevoTipo));
      if (!tipo || !tipo.CodigoCargo) {
        Modal.warning({
          title: 'Tipo sin cargo asignado',
          content: (
            <div>
              <p>El tipo de cancelación seleccionado <strong>{tipo?.Descripcion || nuevoTipo}</strong> no tiene cargo asignado.</p>
              <p style={{ fontSize: '16px', fontWeight: 'bold', color: '#faad14' }}>
                No se puede facturar un tipo de cancelación sin cargo asignado.
              </p>
            </div>
          ),
          width: 600
        });
        return;
      }
      
      setActualizandoTipo(true);
      
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${BASE_URL}/api/reservas/${reserva.NumReserva}/tipo-cancelacion`,
        { tipoCancelacion: nuevoTipo },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        message.success('Tipo de cancelación actualizado correctamente');
        
        setTipoSeleccionado(nuevoTipo);
        setTipoOriginal(nuevoTipo);
        setEditandoTipo(false);
        setTipoInfoActual(tipo);
        
        form.setFieldsValue({ tipoCancelacion: nuevoTipo });
        
        if (onTipoCancelacionActualizado) {
          onTipoCancelacionActualizado({
            ...reserva,
            TipoCancelacion: nuevoTipo
          });
        }
        
        await cargarDatosReserva(reserva.NumReserva);
      }
      
    } catch (error) {
      console.error('Error actualizando tipo de cancelación:', error);
      message.error(error.response?.data?.error || 'Error al actualizar el tipo de cancelación');
    } finally {
      setActualizandoTipo(false);
    }
  };

  // Obtener información del tipo de cancelación actual
  const getTipoInfo = (codigo) => {
    if (!codigo) return null;
    return tiposCancelacion.find(t => String(t.Codigo) === String(codigo)) || null;
  };

  const handleFacturar = async () => {
    try {
      if (facturaYaExiste) {
        setMostrandoDetalleFactura(true);
        return;
      }
      
      const values = await form.validateFields();
      
      if (!reserva.Anulada) {
        Modal.warning({
          title: 'Reserva no cancelada',
          content: (
            <div>
              <p>La reserva no está cancelada.</p>
              <p style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
                Para poder facturar gastos de cancelación, la reserva debe estar cancelada.
              </p>
            </div>
          ),
          width: 600
        });
        return;
      }
      
      const tipoActual = values.tipoCancelacion || reserva.TipoCancelacion;
      
      if (!tipoActual) {
        Modal.warning({
          title: 'Tipo de cancelación requerido',
          content: (
            <div>
              <p>La reserva no tiene un tipo de cancelación asignado.</p>
              <p style={{ fontSize: '16px', fontWeight: 'bold', color: '#faad14' }}>
                Por favor, seleccione un tipo de cancelación antes de facturar.
              </p>
              <Button 
                type="primary" 
                style={{ marginTop: 12 }}
                onClick={() => setEditandoTipo(true)}
              >
                Seleccionar tipo de cancelación
              </Button>
            </div>
          ),
          width: 600
        });
        return;
      }
      
      const tipoInfo = getTipoInfo(tipoActual);
      if (!tipoInfo || !tipoInfo.CodigoCargo) {
        Modal.warning({
          title: 'Tipo de cancelación sin cargo asignado',
          content: (
            <div>
              <p>El tipo de cancelación <strong>{tipoInfo?.Descripcion || tipoActual}</strong> no tiene cargo asignado.</p>
              <p style={{ fontSize: '16px', fontWeight: 'bold', color: '#faad14' }}>
                No se puede facturar un tipo de cancelación sin cargo asignado.
              </p>
              <div style={{ marginTop: 12 }}>
                <Button 
                  type="primary" 
                  onClick={() => setEditandoTipo(true)}
                >
                  <EditOutlined /> Cambiar tipo de cancelación
                </Button>
              </div>
            </div>
          ),
          width: 600
        });
        return;
      }
      
      setPaso(2);
      
    } catch (error) {
      console.error('Error en validación:', error);
      if (error.errorFields) {
        return;
      }
      message.error('Error al procesar la solicitud');
    }
  };

  const handleConfirmar = async () => {
    setLoading(true);
    try {
      const values = await form.validateFields();
      
      const tipoCancelacion = values.tipoCancelacion || reserva.TipoCancelacion;
      
      const token = localStorage.getItem('token');
      const payload = {
        numReserva: reserva.NumReserva,
        importe: importe,
        aQuienFactura: aQuienFactura,
        motivo: values.motivo || `Gastos de cancelación - Reserva #${reserva.NumReserva}`,
        tipoCancelacion: tipoCancelacion
      };
      
      const response = await axios.post(
        `${BASE_URL}/api/reservas/facturar-gastos-cancelacion`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setResultado(response.data);
        setPaso(3);
        
        if (onSuccess) {
          onSuccess(response.data);
        }
        
        message.success('Factura de gastos de cancelación creada correctamente');
      }
      
    } catch (error) {
      console.error('Error facturando:', error);
      
      if (error.response?.data?.detalles) {
        Modal.error({
          title: 'Error al facturar',
          content: (
            <div>
              <Text type="danger">{error.response.data.error}</Text>
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">{error.response.data.detalles}</Text>
              </div>
            </div>
          )
        });
      } else {
        message.error(error.response?.data?.error || 'Error al facturar los gastos de cancelación');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVolver = () => {
    setPaso(1);
  };

  const handleCerrar = () => {
    if (onCancel) onCancel();
  };

  // Renderizar detalle de factura existente (dentro del mismo modal)
  const renderDetalleFacturaExistente = () => {
    const data = facturaExistente;
    console.log(" data => ",data);
    
    if (!data) return null;
    
    return (
      <div>
        <Alert
          message="Esta reserva ya tiene una factura de gastos de cancelación"
          description="No es posible generar una nueva factura de cancelación para esta reserva."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        <Card size="small" title="Datos de la factura">
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Serie">
              <Tag color="blue">{data.Serie}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Nº Factura">
              <Tag color="green">#{data.NFactura}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Ejercicio">
              {data.Ejercicio}
            </Descriptions.Item>
            <Descriptions.Item label="Total">
              <Text strong style={{ color: '#fa8c16', fontSize: '18px' }}>
                {data.total?.toFixed(2)} €
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Fecha" span={2}>
              {data.fecha ? new Date(data.fecha).toLocaleDateString() : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Cliente" span={2}>
              {data.cliente?.nombre || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Dirección" span={2}>
              {data.cliente?.direccion || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="NIF" span={2}>
              {data.cliente?.nif || 'N/A'}
            </Descriptions.Item>
          </Descriptions>
          
          {/* {data.lineas && data.lineas.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Text strong>Líneas de la factura:</Text>
              <Table
                dataSource={data.lineas}
                rowKey={(record, index) => index}
                size="small"
                pagination={false}
                columns={[
                  { title: 'Código', dataIndex: 'codigo', key: 'codigo', width: 100 },
                  { title: 'Descripción', dataIndex: 'descripcion', key: 'descripcion' },
                  { 
                    title: 'Precio', 
                    dataIndex: 'precio', 
                    key: 'precio',
                    render: (val) => val?.toFixed(2) || '0.00',
                    align: 'right'
                  },
                  { 
                    title: 'Cantidad', 
                    dataIndex: 'cantidad', 
                    key: 'cantidad',
                    align: 'right'
                  },
                  { 
                    title: 'Importe', 
                    dataIndex: 'importe', 
                    key: 'importe',
                    render: (val) => <Text strong>{val?.toFixed(2)} €</Text>,
                    align: 'right'
                  }
                ]}
              />
            </div>
          )} */}
        </Card>
        
        <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button 
            type="primary" 
            icon={<PrinterOutlined />}
                onClick={() => handleImprimir(data)}
          >
            Imprimir Factura
          </Button>
          <Button onClick={handleCerrar}>
            Cerrar
          </Button>
        </div>
      </div>
    );
  };

  // Renderizar selector de tipo de cancelación
  const renderTipoCancelacion = () => {
    const tipoActual = form.getFieldValue('tipoCancelacion') || reserva?.TipoCancelacion;
    const tipoInfo = tipoInfoActual || getTipoInfo(tipoActual);
    
    return (
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text strong>Tipo de Cancelación</Text>
          {!editandoTipo && tipoActual && !facturaYaExiste && (
            <Button 
              type="link" 
              size="small" 
              icon={<EditOutlined />}
              onClick={() => setEditandoTipo(true)}
            >
              Cambiar
            </Button>
          )}
        </div>
        
        {editandoTipo ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 8 }}>
            <Form.Item
              name="tipoCancelacion"
              style={{ flex: 1, marginBottom: 0 }}
              rules={[{ required: true, message: 'Seleccione un tipo de cancelación' }]}
            >
              <Select
                placeholder="Seleccionar tipo de cancelación"
                loading={cargandoTipos}
                showSearch
                optionFilterProp="label"
                filterOption={(input, option) => {
                  const label = option?.label?.toLowerCase() || '';
                  return label.includes(input.toLowerCase());
                }}
                onChange={(value) => {
                  setTipoSeleccionado(value);
                  const tipo = tiposCancelacion.find(t => String(t.Codigo) === String(value));
                  setTipoInfoActual(tipo || null);
                  if (tipo && !tipo.CodigoCargo) {
                    message.warning('Este tipo de cancelación no tiene cargo asignado');
                  }
                }}
                value={tipoSeleccionado}
              >
                {tiposCancelacion.map(tipo => (
                  <Option 
                    key={tipo.Codigo} 
                    value={tipo.Codigo}
                    label={`${tipo.Codigo} - ${tipo.Descripcion}`}
                  >
                    <Space>
                      <span>{tipo.Codigo} - {tipo.Descripcion}</span>
                      {tipo.CodigoCargo ? (
                        <Tag color="green" size="small">Con cargo: {tipo.CodigoCargo}</Tag>
                      ) : (
                        <Tag color="red" size="small">Sin cargo</Tag>
                      )}
                    </Space>
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleActualizarTipoCancelacion}
              loading={actualizandoTipo}
            >
              Guardar
            </Button>
            <Button
              onClick={() => {
                setEditandoTipo(false);
                const valorOriginal = tipoOriginal || reserva?.TipoCancelacion || null;
                form.setFieldsValue({ tipoCancelacion: valorOriginal });
                setTipoSeleccionado(valorOriginal);
                const info = tiposCancelacion.find(t => String(t.Codigo) === String(valorOriginal));
                setTipoInfoActual(info || null);
              }}
            >
              Cancelar
            </Button>
          </div>
        ) : (
          <div style={{ marginTop: 8 }}>
            {tipoActual ? (
              <div>
                <Tag color={tipoInfo?.CodigoCargo ? 'blue' : 'orange'} style={{ fontSize: '14px', padding: '4px 12px' }}>
                  <strong>{tipoActual}</strong>
                </Tag>
                <Text style={{ marginLeft: 8 }}>
                  {tipoInfo?.Descripcion || 'Sin descripción'}
                </Text>
                {tipoInfo?.CodigoCargo ? (
                  <Tag color="green" style={{ marginLeft: 8 }}>✓ Con cargo: {tipoInfo.CodigoCargo}</Tag>
                ) : (
                  <Tag color="red" style={{ marginLeft: 8 }}>✗ Sin cargo</Tag>
                )}
              </div>
            ) : (
              <div>
                <Alert
                  message="Sin tipo de cancelación asignado"
                  description="Seleccione un tipo de cancelación para poder facturar."
                  type="warning"
                  showIcon
                  action={
                    <Button 
                      type="primary" 
                      size="small"
                      onClick={() => setEditandoTipo(true)}
                    >
                      Seleccionar
                    </Button>
                  }
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Renderizar paso 1: Datos de la factura
  const renderPasoDatos = () => {
    // Si ya existe factura, mostrar el detalle directamente
    if (facturaYaExiste && mostrandoDetalleFactura) {
      return renderDetalleFacturaExistente();
    }
    
    return (
      <div>
        {/* Información de la reserva */}
        <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f0f5ff' }}>
          <Row gutter={[16, 8]}>
            <Col span={8}>
              <Text strong>Reserva: </Text>
              <Tag color="blue">#{reserva?.NumReserva}</Tag>
            </Col>
            <Col span={8}>
              <Text strong>Cliente: </Text>
              <Text>{reserva?.Nombre || 'N/A'}</Text>
            </Col>
            <Col span={8}>
              <Text strong>Estado: </Text>
              <Tag color="red">CANCELADA</Tag>
            </Col>
            <Col span={12}>
              <Text strong>Entrada: </Text>
              <Text>{reserva?.Entrada ? new Date(reserva.Entrada).toLocaleDateString() : 'N/A'}</Text>
            </Col>
            <Col span={12}>
              <Text strong>Salida: </Text>
              <Text>{reserva?.Salida ? new Date(reserva.Salida).toLocaleDateString() : 'N/A'}</Text>
            </Col>
          </Row>
        </Card>

        {/* Selector de tipo de cancelación */}
        {renderTipoCancelacion()}

        <Form
          form={form}
          layout="vertical"
          initialValues={{
            importe: 0,
            aQuien: 'Particular',
            motivo: `Gastos de cancelación - Reserva #${reserva?.NumReserva || ''}`,
            tipoCancelacion: reserva?.TipoCancelacion ? String(reserva.TipoCancelacion) : null
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="importe"
                label="Importe de la Cancelación"
                rules={[
                  { required: true, message: 'Por favor ingrese el importe' },
                  { 
                    validator: (_, value) => {
                      if (value > 0) return Promise.resolve();
                      return Promise.reject('El importe debe ser mayor que 0');
                    }
                  }
                ]}
                tooltip="Importe total de la cancelación"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0.01}
                  step={0.01}
                  precision={2}
                  placeholder="0.00"
                  prefix={<DollarOutlined />}
                  suffix="€"
                  size="large"
                  onChange={(value) => setImporte(value || 0)}
                />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="aQuien"
                label="Facturar a"
                rules={[{ required: true, message: 'Seleccione a quien facturar' }]}
              >
                <Select
                  size="large"
                  onChange={(value) => setAQuienFactura(value)}
                >
                  <Option value="Agencia">
                    <BankOutlined /> Agencia
                  </Option>
                  <Option value="Particular">
                    <UserOutlined /> Particular
                  </Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="motivo"
            label="Motivo / Observación"
            rules={[{ required: true, message: 'Por favor ingrese el motivo' }]}
          >
            <TextArea
              rows={3}
              placeholder="Motivo de la cancelación"
              maxLength={255}
              showCount
            />
          </Form.Item>

          <Alert
            message="Información importante"
            description={
              <div>
                <p>• El importe facturado se cargará en la cuenta del cliente.</p>
                <p>• La factura se creará con el tipo <strong>CAN</strong> (Cancelación).</p>
                <p>• Se registrará en el diario de la reserva.</p>
                {!editandoTipo && reserva?.TipoCancelacion && (
                  <p>• Tipo de cancelación: <strong>{tipoInfoActual?.Descripcion || reserva.TipoCancelacion}</strong></p>
                )}
              </div>
            }
            type="info"
            showIcon
            style={{ marginTop: 8 }}
          />
        </Form>
      </div>
    );
  };

  // Renderizar paso 2: Confirmación
  const renderPasoConfirmacion = () => {
    const values = form.getFieldsValue();
    const tipoActual = values.tipoCancelacion || reserva?.TipoCancelacion;
    const tipoInfo = tipoInfoActual || getTipoInfo(tipoActual);
    
    return (
      <div>
        <Alert
          message="Confirmar facturación"
          description="¿Está seguro de que desea facturar los gastos de cancelación?"
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        <Card size="small" title="Resumen de la facturación">
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Reserva">
              <Tag color="blue">#{reserva?.NumReserva}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Cliente">
              {reserva?.Nombre || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Tipo Cancelación" span={2}>
              <Tag color={tipoInfo?.CodigoCargo ? 'blue' : 'orange'}>
                {tipoActual || 'N/A'}
              </Tag>
              {tipoInfo && (
                <Text style={{ marginLeft: 8 }}>
                  {tipoInfo.Descripcion}
                  {tipoInfo.CodigoCargo ? (
                    <Tag color="green" style={{ marginLeft: 8 }}>Con cargo: {tipoInfo.CodigoCargo}</Tag>
                  ) : (
                    <Tag color="red" style={{ marginLeft: 8 }}>Sin cargo</Tag>
                  )}
                </Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Facturar a" span={2}>
              <Tag color="purple">{values.aQuien}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Importe" span={2}>
              <Text strong style={{ color: '#fa8c16', fontSize: '20px' }}>
                {values.importe?.toFixed(2)} €
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Motivo" span={2}>
              {values.motivo}
            </Descriptions.Item>
          </Descriptions>
        </Card>
        
        <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button onClick={handleVolver}>
            Volver
          </Button>
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={handleConfirmar}
            loading={loading}
          >
            Confirmar Facturación
          </Button>
        </div>
      </div>
    );
  };

  // Renderizar paso 3: Resultado
  const renderPasoResultado = () => (
    <div>
      <Alert
        message="Factura creada correctamente"
        description="La factura de gastos de cancelación se ha generado exitosamente."
        type="success"
        showIcon
        style={{ marginBottom: 16 }}
      />
      
      {resultado?.data?.factura && (
        <Card size="small" title="Datos de la factura">
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Serie">
              <Tag color="blue">{resultado.data.factura.Serie}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Nº Factura">
              <Tag color="green">#{resultado.data.factura.NFactura}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Ejercicio">
              {resultado.data.factura.Ejercicio}
            </Descriptions.Item>
            <Descriptions.Item label="Total">
              <Text strong style={{ color: '#fa8c16', fontSize: '18px' }}>
                {resultado.data.factura.total?.toFixed(2)} €
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Fecha" span={2}>
              {resultado.data.factura.fecha ? new Date(resultado.data.factura.fecha).toLocaleDateString() : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Cliente" span={2}>
              {resultado.data.factura.nombreCliente || 'N/A'}
            </Descriptions.Item>
          </Descriptions>
          
          {/* {resultado.data.facturaCompleta && resultado.data.facturaCompleta.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Text strong>Líneas de la factura:</Text>
              <Table
                dataSource={resultado.data.facturaCompleta}
                rowKey="Linea"
                size="small"
                pagination={false}
                columns={[
                  { title: 'Código', dataIndex: 'Codigo', key: 'Codigo', width: 100 },
                  { title: 'Descripción', dataIndex: 'Descripcion', key: 'Descripcion' },
                  { 
                    title: 'Precio', 
                    dataIndex: 'Precio', 
                    key: 'Precio',
                    render: (val) => val?.toFixed(2) || '0.00',
                    align: 'right'
                  },
                  { 
                    title: 'Cantidad', 
                    dataIndex: 'Cantidad', 
                    key: 'Cantidad',
                    align: 'right'
                  },
                  { 
                    title: 'Importe', 
                    dataIndex: 'Importe', 
                    key: 'Importe',
                    render: (val) => <Text strong>{val?.toFixed(2)} €</Text>,
                    align: 'right'
                  }
                ]}
              />
            </div>
          )} */}
        </Card>
      )}
      
      <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button
          type="primary"
          icon={<PrinterOutlined />}
          onClick={() => {           
            handleImprimir(resultado.data.factura)
          }}
        >
          Imprimir Factura
        </Button>
        <Button onClick={handleCerrar}>
          Cerrar
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      title={
        <Space>
          <DollarOutlined style={{ color: '#fa8c16' }} />
          <span>Facturar Gastos de Cancelación</span>
          {reserva && (
            <Tag color="red" style={{ marginLeft: 8 }}>
              Reserva #{reserva.NumReserva}
            </Tag>
          )}
          {facturaYaExiste && (
            <Tag color="orange" style={{ marginLeft: 8 }}>
              Ya facturada
            </Tag>
          )}
        </Space>
      }
      open={visible}
      onCancel={handleCerrar}
      footer={null}
      width={facturaYaExiste && mostrandoDetalleFactura ? 800 : 700}
      destroyOnClose
      style={{ top: 20 }}
      bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
    >
      {cargandoReserva || verificandoFactura ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
          <p style={{ marginTop: 16 }}>
            {verificandoFactura ? 'Verificando factura existente...' : 'Cargando datos de la reserva...'}
          </p>
        </div>
      ) : (
        <>
          {paso === 1 && renderPasoDatos()}
          {paso === 2 && renderPasoConfirmacion()}
          {paso === 3 && renderPasoResultado()}
          
          {paso === 1 && !facturaYaExiste && (
            <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button onClick={handleCerrar}>
                Cancelar
              </Button>
              <Button
                type="primary"
                icon={<DollarOutlined />}
                onClick={handleFacturar}
                loading={loading}
              >
                Facturar
              </Button>
            </div>
          )}
          
          {/* {paso === 1 && facturaYaExiste && mostrandoDetalleFactura && (
            <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button 
                type="primary" 
                icon={<PrinterOutlined />}
                onClick={() => {
                  if (onImprimir && facturaExistente) {
                    onImprimir(facturaExistente);
                  }
                }}
              >
                Imprimir Factura....
              </Button>
              <Button onClick={handleCerrar}>
                Cerrar
              </Button>
            </div>
          )} */}
        </>
      )}
    </Modal>
  );
};

export default FacturarGastosCancelacion;